import React, { useCallback, useMemo, useState } from 'react';

import { LineChart } from '@/components/shared/LineChart';
import { ModalSectionCard } from '@/components/shared/ModalSectionCard';
import { ModalSectionTitle } from '@/components/shared/ModalSectionTitle';
import { ChartSectionData } from '@/helpers/reportsDataHelpers';
import { SentimentHistoryPoint } from '@/hooks/useReportsData';

import { ChartEmptyState } from '../ChartEmptyState/ChartEmptyState';
import styles from './SentimentHistoryChart.module.scss';

const BLUE_700 = '#0040ff';

const SERIES = [
	{ id: 'personal',     label: 'Personal',     color: '#ffaa9c' },
	{ id: 'team',         label: 'Team',         color: '#c7ff4e' },
	{ id: 'congregation', label: 'Congregation', color: '#cec1f1' },
] as const;

type SeriesId = typeof SERIES[number]['id'] | 'trend';

function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r},${g},${b},${alpha})`;
}

// Default nivo/line layer stack — trend layer is appended after so it renders on top.
const DEFAULT_LAYERS = [
	'grid', 'markers', 'axes', 'areas', 'crosshair', 'lines', 'points', 'slices', 'mesh', 'legends',
] as const;

type Props = {
	section: ChartSectionData<SentimentHistoryPoint>;
};

export const SentimentHistoryChart: React.FC<Props> = ({ section }) => {
	const [hoveredSeries, setHoveredSeries] = useState<SeriesId | null>(null);

	// Group same-day setlists into one slot (average scores).
	const groupedData = useMemo(() => {
		const byDate = new Map<string, { personal: number[]; team: number[]; congregation: number[] }>();
		for (const p of section.data) {
			const g = byDate.get(p.date) ?? { personal: [], team: [], congregation: [] };
			g.personal.push(p.personalScore);
			g.team.push(p.teamScore);
			g.congregation.push(p.congregationalScore);
			byDate.set(p.date, g);
		}
		const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
		return [...byDate.keys()].sort().map((date) => {
			const g = byDate.get(date)!;
			return {
				date,
				personalScore: avg(g.personal),
				teamScore: avg(g.team),
				congregationalScore: avg(g.congregation),
			};
		});
	}, [section.data]);

	// Compute linear regression endpoints over (personal + team + congregation) / 3.
	const trendLine = useMemo(() => {
		const n = groupedData.length;
		if (n < 2) return null;

		const ys = groupedData.map((p) => (p.personalScore + p.teamScore + p.congregationalScore) / 3);
		const sumX = (n * (n - 1)) / 2;
		const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
		const sumY = ys.reduce((a, b) => a + b, 0);
		const sumXY = ys.reduce((acc, y, i) => acc + i * y, 0);

		const denom = n * sumX2 - sumX * sumX;
		if (denom === 0) return null;

		const slope = (n * sumXY - sumX * sumY) / denom;
		const intercept = (sumY - slope * sumX) / n;

		return {
			startX: groupedData[0].date,
			startY: intercept,
			endX: groupedData[n - 1].date,
			endY: slope * (n - 1) + intercept,
		};
	}, [groupedData]);

	// Custom layer renders the regression line — dims when a data series is hovered.
	const trendLayer = useMemo(() => {
		if (!trendLine) return null;
		const { startX, startY, endX, endY } = trendLine;
		const dimmed = hoveredSeries !== null && hoveredSeries !== 'trend';
		return function TrendLineLayer({ xScale, yScale }: { xScale: (v: unknown) => number; yScale: (v: unknown) => number }) {
			return (
				<line
					x1={xScale(startX)}
					y1={yScale(startY)}
					x2={xScale(endX)}
					y2={yScale(endY)}
					stroke={dimmed ? hexToRgba(BLUE_700, 0.15) : BLUE_700}
					strokeWidth={2}
				/>
			);
		};
	}, [trendLine, hoveredSeries]);

	const layers = useMemo(() => {
		if (!trendLayer) return [...DEFAULT_LAYERS];
		const result = [...DEFAULT_LAYERS] as any[];
		const insertBefore = hoveredSeries === 'trend' ? 'slices' : 'lines';
		result.splice(DEFAULT_LAYERS.indexOf(insertBefore), 0, trendLayer);
		return result;
	}, [trendLayer, hoveredSeries]);

	// Sparse tick dates — show ~6 labels regardless of range length.
	const tickValues = useMemo(() => {
		const step = Math.max(1, Math.ceil(groupedData.length / 6));
		return groupedData.filter((_, i) => i % step === 0).map((p) => p.date);
	}, [groupedData]);

	const baseData = useMemo(() => [
		{ id: 'personal',     data: groupedData.map((p) => ({ x: p.date, y: p.personalScore })) },
		{ id: 'team',         data: groupedData.map((p) => ({ x: p.date, y: p.teamScore })) },
		{ id: 'congregation', data: groupedData.map((p) => ({ x: p.date, y: p.congregationalScore })) },
	], [groupedData]);

	// Nivo's Lines layer reverses series before rendering (first item ends up on top in SVG).
	// Put hovered series first so after reversal it renders last = on top.
	// Named serie.id keys let React physically reorder DOM nodes when order changes.
	const orderedData = useMemo(() => {
		if (!hoveredSeries || hoveredSeries === 'trend') return baseData;
		const hovered = baseData.find((s) => s.id === hoveredSeries)!;
		const others = baseData.filter((s) => s.id !== hoveredSeries);
		return [hovered, ...others];
	}, [baseData, hoveredSeries]);

	// Parallel color array matching orderedData — nivo ordinal scale assigns colors by data order.
	const orderedColors = useMemo(() => {
		return orderedData.map((s) => {
			const def = SERIES.find((d) => d.id === s.id)!;
			if (!hoveredSeries || hoveredSeries === s.id) return def.color;
			return hexToRgba(def.color, 0.15);
		});
	}, [orderedData, hoveredSeries]);

	if (section.emptyReason !== null) {
		return (
			<ModalSectionCard paddingSize="lg">
				<ModalSectionTitle>Sentiment History</ModalSectionTitle>
				<ChartEmptyState emptyReason={section.emptyReason ?? 'no-history'} />
			</ModalSectionCard>
		);
	}

	return (
		<ModalSectionCard paddingSize="lg">
			<ModalSectionTitle>Sentiment History</ModalSectionTitle>
			<LineChart
				wrapperClassName={styles['chart']}
				data={orderedData}
				emojiYAxis
				colors={orderedColors}
				layers={layers as any}
				axisBottom={{
					tickValues,
					format: (v) => {
						const d = new Date((v as string) + 'T00:00:00');
						return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
					},
				}}
				theme={{
					text: { fill: '#23263b' },
					axis: { ticks: { text: { fill: '#777aaf' } } },
					grid: { line: { stroke: '#cfdcf1', strokeDasharray: 0 } },
					crosshair: { line: { stroke: '#23263b', strokeDasharray: '0' } },
				}}
			/>
			<div className={styles['legend']}>
				{SERIES.map((s) => (
					<div
						key={s.id}
						className={styles['legend-item']}
						style={{ opacity: hoveredSeries && hoveredSeries !== s.id ? 0.3 : 1, cursor: 'default' }}
						onMouseEnter={() => setHoveredSeries(s.id)}
						onMouseLeave={() => setHoveredSeries(null)}
					>
						<div className={styles['legend-circle']} style={{ background: s.color }} />
						<span className={styles['legend-label']}>{s.label}</span>
					</div>
				))}
				{trendLine && (
					<div
						className={styles['legend-item']}
						style={{ opacity: hoveredSeries && hoveredSeries !== 'trend' ? 0.3 : 1, cursor: 'default' }}
						onMouseEnter={() => setHoveredSeries('trend')}
						onMouseLeave={() => setHoveredSeries(null)}
					>
						<div className={styles['legend-circle']} style={{ background: BLUE_700 }} />
						<span className={styles['legend-label']}>Trend</span>
					</div>
				)}
			</div>
		</ModalSectionCard>
	);
};
